import { useState, useEffect, useCallback } from "react";
import { Stack, Button, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { Image } from "mui-image";
import CertificatePreview from "../Common/CertificatePreview";
import { ContentSliderProps } from "../../types";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Certificate } from "../../types";

export default function ContentSlider({ orgInfo, customerScanDetails, isExpired, loading }: ContentSliderProps) {
    const [currentSlide, setCurrentSlide] = useState<number>(0);
    const [openCertificateModal, setOpenCertificateModal] = useState<boolean>(false);
    const [selectedCertificate, setSelectedCertificate] = useState<Certificate[]>([]);

    // Number of slides
    const slideCount = isExpired || loading ? 1 : 2;

    const goToNextSlide = useCallback(() => {
        setCurrentSlide((prevSlide) => (prevSlide + 1) % slideCount);
    }, [slideCount]);

    const goToPrevSlide = useCallback(() => {
        setCurrentSlide((prevSlide) => (prevSlide - 1 + slideCount) % slideCount);
    }, [slideCount]);

    // Auto-play functionality
    useEffect(() => {
        if (isExpired) return;

        const interval = setInterval(() => {
            goToNextSlide();
        }, 5000);

        return () => clearInterval(interval);
    }, [isExpired, goToNextSlide]);

    const handleOpenCertificate = (certificates: Certificate[]) => {
        setSelectedCertificate(certificates);
        setOpenCertificateModal(true);
    }

    // Slide content components
    const OrganizationSlide = () => (
        <Stack sx={{ direction: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "center" }} spacing={2}>
            <Stack display={"flex"} direction={"row"} justifyContent={"center"}>
                <Box
                    component="img"
                    src={orgInfo?.logoPresignedUrl}
                    alt=""
                    sx={{ width: { xs: "160px", md: "200px", }, height: "auto", alignSelf: "center", }}
                />
            </Stack>
            <Stack direction="column" flex={1} sx={{ alignItems: "center" }} maxWidth={"100%"}>
                <Typography variant="subtitle1" fontWeight={600} textAlign={"center"} sx={{ wordBreak: "break-word" }}>{orgInfo?.tagLine}</Typography>
                <Typography variant="body2" textAlign={"center"} sx={{ wordBreak: "break-word" }} >{orgInfo?.storyLine}</Typography>
                {
                    orgInfo?.organizationCertificates.length > 0 && (
                        <Button
                            onClick={() => handleOpenCertificate(orgInfo?.organizationCertificates)}
                            sx={{
                                color: "#0073e6",
                                textDecoration: "none",
                                cursor: "pointer",
                                '&:hover': {
                                    textDecoration: "underline",
                                    color: "#005bb5"
                                },
                                fontWeight: 500,
                            }}
                        >
                            Our certifications
                        </Button>
                    )
                }
            </Stack>
        </Stack>
    );

    const CustomerScanSlide = () => (
        <Box maxWidth={"100%"}>
            <Stack direction="column" flex={1} sx={{ alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ maxWidth: "230px" }}>
                    <Image
                        src={customerScanDetails?.region?.engagementImage}
                        alt="banner"
                        xs={{ width: "100px", height: "100%" }}
                    />
                </Box>
                <Typography variant="body2" textAlign={"center"} mt={2} sx={{ wordBreak: "break-word" }}>
                    {customerScanDetails?.region?.engagementMessage}
                </Typography>
            </Stack>
        </Box>
    );

    return (
        <>
            <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', py: 2 }}>
                {!isExpired && slideCount > 1 && (
                    <IconButton
                        onClick={goToPrevSlide}
                        sx={{
                            position: 'relative',
                            zIndex: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                            mr: 1
                        }}
                    >
                        <ArrowBackIosNewIcon />
                    </IconButton>
                )}

                <Box sx={{ position: 'relative', overflow: 'hidden', flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Box
                        sx={{
                            display: 'flex',
                            transition: 'transform 0.5s ease-in-out',
                            transform: `translateX(-${currentSlide * 100}%)`,
                        }}
                    >
                        <Box sx={{ minWidth: '100%', flex: '0 0 100%' }}>
                            <OrganizationSlide />
                        </Box>

                        {!isExpired && !loading && (
                            <Box sx={{ minWidth: '100%', flex: '0 0 100%' }}>
                                <CustomerScanSlide />
                            </Box>
                        )}
                    </Box>
                </Box>

                {!isExpired && slideCount > 1 && (
                    <IconButton
                        onClick={goToNextSlide}
                        sx={{
                            position: 'relative',
                            zIndex: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                            ml: 1
                        }}
                    >
                        <ArrowForwardIosIcon />
                    </IconButton>
                )}
            </Box>

            {openCertificateModal &&
                <CertificatePreview
                    open={openCertificateModal}
                    onClose={() => setOpenCertificateModal(false)}
                    certificates={selectedCertificate}
                />
            }
        </>
    );
}
